export const LAW_URL =
  "https://eli.gov.pl/api/acts/DU/2026/783/text/O/D20260783.pdf";
export const reasons = {
  art28: {
    title: "Z końcem okresu ubezpieczenia",
    article: "art. 28 ust. 1",
    help: "Klient nie chce przedłużyć swojej rocznej umowy OC. Złóż wypowiedzenie najpóźniej dzień przed końcem okresu ubezpieczenia.",
  },
  art28a: {
    title: "Podwójne OC po automatycznym odnowieniu",
    article: "art. 28a ust. 1",
    help: "Klient ma OC w co najmniej dwóch towarzystwach, a wypowiadana umowa odnowiła się automatycznie. TU może pobrać składkę za okres udzielonej ochrony.",
  },
  art31: {
    title: "OC przejęte po zakupie pojazdu",
    article: "art. 31 ust. 1",
    help: "Nabywca wypowiada OC przejęte od poprzedniego właściciela. Wskaż dzień rozwiązania albo wybierz dzień złożenia wypowiedzenia. Zapewnij ciągłość OC.",
  },
} as const;
export type Reason = keyof typeof reasons;
export interface TerminationData {
  reason: Reason;
  name: string;
  address: string;
  insurer: string;
  policy: string;
  brand: string;
  model: string;
  registration: string;
  start: string;
  end: string;
  city: string;
  date: string;
  effectiveDate: string;
  acquisitionDate: string;
  otherInsurer: string;
  otherPolicy: string;
  coOwners: string;
}
export function localDate() {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Warsaw" }).format(
    new Date(),
  );
}
export function datePl(value: string) {
  return value.split("-").reverse().join(".");
}
export interface StatementPart {
  text: string;
  bold?: boolean;
  key?: string;
}
export function statementParts(d: TerminationData): StatementPart[] {
  const text = (value: string): StatementPart => ({ text: value });
  const value = (value: string, key: string): StatementPart => ({
    text: value,
    bold: true,
    key,
  });
  const parts = [
    text(
      `Na podstawie ${reasons[d.reason].article} ustawy z dnia 22 maja 2003 r. o ubezpieczeniach obowiązkowych, Ubezpieczeniowym Funduszu Gwarancyjnym i Polskim Biurze Ubezpieczycieli Komunikacyjnych wypowiadam umowę obowiązkowego ubezpieczenia OC posiadaczy pojazdów mechanicznych o numerze `,
    ),
    value(d.policy, "policy"),
    text(", zawartą z "),
    value(d.insurer, "insurer"),
    text(", dotyczącą wskazanego pojazdu"),
  ];
  if (d.reason === "art28") {
    return [
      ...parts,
      text(", z końcem okresu ubezpieczenia, tj. z dniem "),
      value(datePl(d.end), "end"),
      text("."),
    ];
  }
  if (d.reason === "art28a") {
    return [
      ...parts,
      text(
        ". Wypowiadana umowa została zawarta w wyniku automatycznego odnowienia na podstawie art. 28 ust. 1. Jednocześnie pojazd jest ubezpieczony w ",
      ),
      value(d.otherInsurer, "otherInsurer"),
      text(", polisa nr "),
      value(d.otherPolicy, "otherPolicy"),
      text("."),
    ];
  }
  return [
    ...parts,
    text(", przejętą po nabyciu pojazdu w dniu "),
    value(datePl(d.acquisitionDate), "acquisitionDate"),
    text(". Umowę wypowiadam "),
    ...(d.effectiveDate
      ? [
          text("ze skutkiem na dzień "),
          value(datePl(d.effectiveDate), "effectiveDate"),
        ]
      : [text("ze skutkiem na dzień złożenia niniejszego wypowiedzenia")]),
    text("."),
  ];
}
export function statement(d: TerminationData) {
  return statementParts(d)
    .map((part) => part.text)
    .join("");
}
export function validate(d: TerminationData): string | null {
  const required: (keyof TerminationData)[] = [
    "name",
    "address",
    "insurer",
    "policy",
    "brand",
    "model",
    "registration",
    "start",
    "end",
    "city",
    "date",
  ];
  if (d.reason === "art31") {
    required.push("acquisitionDate");
  }
  if (d.reason === "art28a") {
    required.push("otherInsurer", "otherPolicy");
  }
  if (required.some((k) => !d[k].trim())) {
    return "Uzupełnij wszystkie wymagane pola.";
  }
  if (Object.values(d).some((v) => v.length > 300)) {
    return "Pole może zawierać maksymalnie 300 znaków.";
  }
  if (d.start > d.end) {
    return "Koniec ochrony nie może być przed jej początkiem.";
  }
  if (
    d.reason === "art31" &&
    (d.acquisitionDate > d.date || d.acquisitionDate > d.end)
  ) {
    return "Sprawdź datę nabycia pojazdu.";
  }
  if (d.reason === "art31" && d.effectiveDate && d.effectiveDate > d.end) {
    return "Dzień rozwiązania nie może przypadać po końcu ochrony.";
  }
  return null;
}
